import { Table, UnstyledButton } from '@mantine/core';
import { useCallback, useMemo } from 'react';
import type { EntityType } from '../../domain/labels.js';
import type { SyncStatus } from '../../domain/status.js';
import { SyncStatusBadge } from '../StatusBadge/StatusBadge.js';
import { cellTargets } from './cellTargets.js';
import type { MatrixColumn, OpenTarget } from './types.js';

/**
 * One status cell per environment column — a row's own status there, opening the drawer on
 * click. `MatrixGrid`'s status cells (`MatrixRow`'s memoised body), so this only re-runs when
 * the row it belongs to actually changes: `cellTargets` is memoised on its own inputs, and
 * `onOpen` is wrapped once rather than rebuilt every render.
 */
export const StatusCells = ({
    columns,
    cells,
    type,
    entityKey,
    name,
    onOpen,
}: {
    columns: MatrixColumn[];
    cells: Record<string, { status: SyncStatus } | undefined>;
    type: EntityType;
    entityKey: string;
    /** The row's display name, for the cell's `aria-label`. */
    name: string;
    onOpen: (target: OpenTarget) => void;
}) => {
    const targets = useMemo(
        () => cellTargets(columns, cells, type, entityKey, name),
        [columns, cells, type, entityKey, name],
    );
    const open = useCallback((target: OpenTarget) => onOpen(target), [onOpen]);

    return (
        <>
            {targets.map((cell) => (
                <Table.Td key={cell.environment}>
                    <UnstyledButton onClick={() => open(cell.target)} aria-label={cell.label}>
                        <SyncStatusBadge status={cell.status} iconOnly size="sm" />
                    </UnstyledButton>
                </Table.Td>
            ))}
        </>
    );
};
